'use client';

import Image from 'next/image';
import Link from 'next/link';
import SmoothScroller from '@/app/components/SmoothScroller';
import { Button } from '@heroui/react';
import { useGSAP } from '@gsap/react';
import React, { useEffect, useState } from 'react';
import gsap from 'gsap';
import { FaFacebook, FaInstagram } from 'react-icons/fa';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { motion } from 'framer-motion';

type NavLink = { label: string, href: string };

const SUPPLIERS_PATH = '/pro-dodavatele';

const suppliersLinks: NavLink[] = [
  { label: 'Proč my', href: '/pro-dodavatele#proc-my' },
  { label: 'Kde je zastihneme', href: '/pro-dodavatele#kde-zastihneme' },
  { label: 'Co uděláme', href: '/pro-dodavatele#co-udelame' },
  { label: 'Kontakt', href: '/pro-dodavatele#kontakt' },
  { label: 'Pro restaurace', href: '/' },
];

const homeDesktopLinks: NavLink[] = [
  { label: 'Proč s námi', href: '/#proc-s-nami' },
  { label: 'Jak pracujeme', href: '/#jak-pracujeme' },
  { label: 'Ukázky práce', href: '/#ukazky-prace' },
  { label: 'Kontakt', href: '/#zaciname' },
  { label: 'Pro dodavatele', href: '/pro-dodavatele' },
];

const homeMobileLinks: NavLink[] = [
  { label: 'Co děláme', href: '/#co-umime' },
  { label: 'Ukázky práce', href: '/#ukazky-prace' },
  { label: 'Proč s námi', href: '/#proc-s-nami' },
  { label: 'Jak pracujeme', href: '/#jak-pracujeme' },
  { label: 'Kontakt', href: '/#zaciname' },
  { label: 'Pro dodavatele', href: '/pro-dodavatele' },
];

export default function Navbar({}: {}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMobileMenu, setOpenMobileMenu] = useState(null as GSAPTimeline | null);
  const [closeMobileMenu, setCloseMobileMenu] = useState(null as GSAPTimeline | null);

  const mobileMenuContainerRef = React.useRef<HTMLDivElement>(null);

  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const isSuppliersPage = pathname === SUPPLIERS_PATH;
  const desktopLinks = isSuppliersPage ? suppliersLinks : homeDesktopLinks;
  const mobileLinks = isSuppliersPage ? suppliersLinks : homeMobileLinks;
  const ctaLink: NavLink = isSuppliersPage
    ? { label: 'Chci návrh zdarma', href: '/pro-dodavatele#kontakt' }
    : { label: 'Mám zájem', href: '/#zaciname' };

  useEffect(() => {
    if (searchParams.get('utm_source') === 'leaflet' && !location.href.includes('e-book'))
    {
      // replace with /e-book, but keep the query params
      const urlWithParams = new URL(location.href);
      urlWithParams.pathname = '/e-book';
      router.replace(urlWithParams.toString(), { scroll: false });
    }
  });

  useGSAP(() => {
    const openMobileMenu = gsap.timeline({ paused: true });
    openMobileMenu.to(mobileMenuContainerRef.current, { display: 'block', duration: 0.03 });
    openMobileMenu.to(mobileMenuContainerRef.current, { opacity: 1, duration: 0.2 });
    openMobileMenu.to('.mobile-menu-toggle div:nth-child(1)', { y: 7, duration: 0.2 }, '<');
    openMobileMenu.to('.mobile-menu-toggle div:nth-child(3)', { y: -7, duration: 0.2 }, '<');
    openMobileMenu.to('.mobile-menu-toggle div:nth-child(2)', { opacity: 0, duration: 0.2 }, '<');
    openMobileMenu.to('.mobile-menu-toggle div:nth-child(1)', { rotate: 45, duration: 0.2 });
    openMobileMenu.to('.mobile-menu-toggle div:nth-child(3)', { rotate: -45, duration: 0.2 }, '<');
    setOpenMobileMenu(openMobileMenu);

    const closeMobileMenu = gsap.timeline({ paused: true });
    closeMobileMenu.to('.mobile-menu-toggle div:nth-child(1)', { rotate: 0, duration: 0.2 });
    closeMobileMenu.to('.mobile-menu-toggle div:nth-child(3)', { rotate: 0, duration: 0.2 }, '<');
    closeMobileMenu.to('.mobile-menu-toggle div:nth-child(1)', { y: 0, duration: 0.2 });
    closeMobileMenu.to('.mobile-menu-toggle div:nth-child(3)', { y: 0, duration: 0.2 }, '<');
    closeMobileMenu.to('.mobile-menu-toggle div:nth-child(2)', { opacity: 1, duration: 0.2 }, '<');
    closeMobileMenu.to(mobileMenuContainerRef.current, { opacity: 0, duration: 0.2 }, '<');
    closeMobileMenu.to(mobileMenuContainerRef.current, { display: 'none', duration: 0.03 });
    setCloseMobileMenu(closeMobileMenu);
  });

  const toggleMobileMenu = () => {
    if (mobileMenuOpen)
    {
      closeMobileMenu?.play(0);
    }
    else
    {
      openMobileMenu?.play(0);
    }
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return <>
    <div className={ 'container fixed z-50 flex transform -translate-x-1/2' } style={{marginLeft: '50%'}}>
      <motion.div
        className={ 'h-16 md:h-24 bg-secondary top-2 2xl:top-4 relative flex flex-row items-center z-40 justify-between rounded-2xl navbar navbar-desktop w-full' }
        initial={{ y: -150 }}
        animate={{ y: 0 }}
        transition={{type: 'spring', stiffness: 200, damping: 15, duration: 0.6 }}
      >
        <div className={ 'px-8 xl:px-16 w-full' }>
          <div className={ 'flex flex-row justify-between w-full items-center' }>
            <SmoothScroller href={ '/' }>
              <div>
                <div className={ 'hidden md:flex items-center gap-2' }>
                  <Image src={ '/logos/logo-black.svg' } alt={ 'Logo' } width={ 96 } height={ 96 } className={ 'h-24' } priority loading={'eager'} />
                  <h1 className={ 'text-black text-3xl font-bold hidden' }>GastroStart</h1>
                </div>
                <Image src={ '/logos/logo-black.svg' } alt={ 'Logo' } width={ 60 } height={ 60 } className={ 'h-16 block md:hidden' } priority loading={'eager'} />
              </div>
            </SmoothScroller>
            <div className={ 'flex flex-row justify-center items-center md:justify-end gap-5' }>
              { desktopLinks.map((link) => (
                <SmoothScroller href={ link.href } key={ link.href }>
                  <button className={ 'hidden md:block' }>{ link.label }</button>
                </SmoothScroller>
              )) }
              {/*<SmoothScroller href={ 'https://restomat.cz' }>*/}
              {/*  <button className={ 'hidden md:block' }>Restomat</button>*/}
              {/*</SmoothScroller>*/}
              <Link href={ 'https://blog.gastrostart.cz' } rel={ 'noopener' } target={ '_blank' } className={ 'hidden md:block blog-link' }>Blog</Link>
              <div className={ 'mobile-menu-toggle  md:hidden flex flex-col gap-1 hover:cursor-pointer' } onClick={ toggleMobileMenu }>
                <div></div>
                <div></div>
                <div></div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>

    {/*MOBILE MENU*/ }
    <div className={ 'fixed w-screen h-screen bg-secondary z-20 backdrop-blur-md top-0 px-8 opacity-0 hidden navbar-mobile' } ref={ mobileMenuContainerRef }>
      <div className={ 'flex flex-col gap-4 items-center justify-between pt-36 pb-36 h-full' }>
        <div className={ 'flex flex-col gap-4 items-center justify-start w-full' }>
          { mobileLinks.map((link) => (
            <SmoothScroller href={ link.href } key={ link.href }>
              <button onClick={ toggleMobileMenu }>{ link.label }</button>
            </SmoothScroller>
          )) }
          {/*<SmoothScroller href={ '/e-book' }>*/}
          {/*  <button onClick={ toggleMobileMenu }>E-Book</button>*/}
          {/*</SmoothScroller>*/}
          {/*<SmoothScroller href={ 'https://restomat.cz' }>*/}
          {/*  <button onClick={ toggleMobileMenu }>Restomat</button>*/}
          {/*</SmoothScroller>*/}
          <Link href={ 'https://blog.gastrostart.cz' } rel={ 'noopener' } target={ '_blank' } className={ '' } onClick={ toggleMobileMenu }>Blog</Link>
        </div>

        <div className={ 'w-full flex flex-col gap-8' }>
          <SmoothScroller href={ ctaLink.href }>
            <Button variant={ 'solid' } size={ 'lg' } color={ 'primary' } className={ 'text-lg font-normal mt-8 w-full' } onPress={ toggleMobileMenu }>{ ctaLink.label }</Button>
          </SmoothScroller>
          <div className={ 'flex gap-4 items-center justify-center text-2xl text-black' }>
            <Link href={ 'https://www.instagram.com/gastrostart.cz/' } target={ '_blank' } rel={ 'noopener noreferrer nofollow' }>
              <FaInstagram size={ 32 } className={ 'text-black' }></FaInstagram>
            </Link>
            <Link href={ 'https://www.facebook.com/people/GastroSTART/61554432171140/' } target={ '_blank' } rel={ 'noopener noreferrer nofollow' }>
              <FaFacebook size={ 32 } className={ 'text-black' }></FaFacebook>
            </Link>
          </div>
        </div>
      </div>
    </div>
  </>;
}
